import { DEVICE, DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import {
  formatEndOfLife,
  convertDeviceModel,
  dateToDuration,
  dateToTimeSince,
  formatTimezone,
} from '@/shared/lib';
import _ from 'lodash';
import { t } from 'ttag';
import { facilitySelection } from '@/entities/facility/@x/device';

/**
 * Builds the sectioned content of the device information drawer from a device record. The general information section shows the facility only when the user supports several, the model tagged 'Auto-event detection enabled' for MCT-capable non-Pro devices at non-Test facilities, firmware, timezone, operator, network mode and lifetime; the last-synced section renders RSSI, lead-on, battery, SD space and charging state plus the last connect as an 'ago' phrase, with a placeholder for missing values.
 *
 * @param device - Device record with facility, model, lastSync telemetry and firstStudyDate.
 * @returns Array of drawer sections, each with a header and its label and value rows.
 */
const formatDataDevice = (device: any) => {
  const generalInfoContent = [];

  if (facilitySelection.userSupportFacilities()?.length > 1 && device.facility) {
    generalInfoContent.push({
      label: t`Facility`,
      value: device.facility?.name || DOUBLE_HYPHEN_BULLET,
    });
  }

  const isBiotresPro = device.model === DEVICE.BIOTRES_PRO;
  const shouldDisplayAutoTag =
    device.facility?.status !== 'Test' && device.canRunBiotresMCT && !isBiotresPro;
  const deviceModelTag = shouldDisplayAutoTag && t`Auto-event detection enabled`;
  const deviceModel = convertDeviceModel(device.model);

  generalInfoContent.push(
    {
      label: t`Model`,
      value: `${deviceModel}${deviceModelTag ? ` (${deviceModelTag})` : ''}`,
    },
    {
      label: t`Firmware version`,
      value: device.lastSync?.fwVersion || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Timezone`,
      value:
        formatTimezone({
          isoDate: new Date() as any,
          timezone: device.lastSync?.timezone,
          facility: device.facility,
        }) || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Operator`,
      value: device.lastSync?.operator || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Network mode`,
      value: device.lastSync?.networkMode || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Device lifetime`,
      value: device.firstStudyDate ? formatEndOfLife(device.firstStudyDate) : DOUBLE_HYPHEN_BULLET,
    },
  );

  const lastSyncedStatusContent = [
    {
      label: t`RSSI`,
      value: device.lastSync?.rssi || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Lead on`,
      value: !_.isNil(device.lastSync?.leadOn)
        ? device.lastSync.leadOn
          ? t`Yes`
          : t`No`
        : DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Battery level`,
      value: device.lastSync?.batteryLevel
        ? `${device.lastSync.batteryLevel}%`
        : DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`SD card free space`,
      value: device.lastSync?.sdFree ? `${device.lastSync.sdFree}%` : DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Charging state`,
      value: !_.isNil(device.lastSync?.isCharging)
        ? device.lastSync.isCharging
          ? t`Charging`
          : t`Not charging`
        : DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Last connect`,
      value: dateToTimeSince(dateToDuration(device.lastSync?.time)) || DOUBLE_HYPHEN_BULLET,
    },
  ];

  const drawerData = [
    {
      header: t`General information`,
      content: generalInfoContent,
    },
    {
      header: t`Last synced status`,
      content: lastSyncedStatusContent,
    },
  ];

  return drawerData;
};

export { formatDataDevice };
